/**
 * GamificationUI — Shared gamification components for Fly N Out
 * Used across Profile, Dashboard, and Community pages.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Zap, Lock, Check, TrendingUp } from 'lucide-react';

// ─── Data Definitions ─────────────────────────────────────────────────────────
export const ALL_RANKS = [
  { name: 'Boarding Pass',   icon: '🎫', xp: 0,    color: '#6B7280', glow: 'rgba(107,114,128,0.3)',  desc: 'You just got your boarding pass. Welcome aboard!' },
  { name: 'Frequent Flyer',  icon: '✈️', xp: 50,   color: '#3B82F6', glow: 'rgba(59,130,246,0.35)', desc: 'You fly enough to know the best seats and lounges.' },
  { name: 'Silver Traveler', icon: '🥈', xp: 150,  color: '#94A3B8', glow: 'rgba(148,163,184,0.4)', desc: 'A seasoned traveler with a story at every airport.' },
  { name: 'Gold Traveler',   icon: '🥇', xp: 300,  color: '#D97706', glow: 'rgba(217,119,6,0.35)',  desc: "You've mastered the art of travel." },
  { name: 'Platinum Flyer',  icon: '💎', xp: 600,  color: '#7C3AED', glow: 'rgba(124,58,237,0.35)', desc: 'An elite traveler known across every terminal.' },
  { name: 'Sky Legend',      icon: '🌟', xp: 1000, color: '#FF4500', glow: 'rgba(255,69,0,0.4)',    desc: 'The pinnacle of the Fly N Out community.' },
];

export const ALL_BADGES = [
  { id: 'New Traveler',           icon: '🌱', color: '#10B981', req: 'Join Fly N Out',          xp: 0,   group: 'welcome' },
  { id: 'First Flight',           icon: '🛫', color: '#3B82F6', req: 'Log your first flight',   xp: 10,  group: 'flights' },
  { id: 'Regular Flyer',          icon: '✈️', color: '#6366F1', req: 'Log 5 flights',           xp: 25,  group: 'flights' },
  { id: 'Frequent Flyer',         icon: '🏆', color: '#D97706', req: 'Log 10 flights',          xp: 50,  group: 'flights' },
  { id: 'Jet Setter',             icon: '🌍', color: '#EF4444', req: 'Log 25 flights',          xp: 100, group: 'flights' },
  { id: 'Sky High',               icon: '🚀', color: '#8B5CF6', req: 'Log 50 flights',          xp: 200, group: 'flights' },
  { id: 'Community Star',         icon: '⭐', color: '#F59E0B', req: 'Share 20 experiences',    xp: 100, group: 'community' },
  { id: 'Gate Guru',              icon: '🚪', color: '#1E3A8A', req: 'Share 10 airport tips',   xp: 80,  group: 'community' },
  { id: 'Layover Legend',         icon: '⏳', color: '#0891B2', req: 'Find 5 layover buddies',  xp: 60,  group: 'community' },
  { id: 'Explorer',               icon: '🗺️', color: '#10B981', req: 'Visit 10 different airports', xp: 100, group: 'community' },
  { id: 'Verified Traveler',      icon: '✅', color: '#1E3A8A', req: 'Complete profile',        xp: 30,  group: 'profile' },
  { id: 'Fly N Out Club Member',  icon: '👑', color: '#FF4500', req: 'Subscribe to Club',       xp: 200, group: 'premium' },
];

/** Compute XP progress percentage within the current rank range */
export function getXpProgress(xp) {
  const current = [...ALL_RANKS].reverse().find(r => xp >= r.xp) || ALL_RANKS[0];
  const idx = ALL_RANKS.findIndex(r => r.name === current.name);
  const next = ALL_RANKS[idx + 1];
  if (!next) return { current, next: null, pct: 100, xpInRank: 0, xpNeeded: 0 };
  const base = current.xp;
  const span = next.xp - base;
  const gained = xp - base;
  const pct = Math.min(100, Math.round((gained / span) * 100));
  return { current, next, pct, xpInRank: gained, xpNeeded: next.xp - xp };
}

// ─── XP Ring ─────────────────────────────────────────────────────────────────
/** Animated circular XP progress ring */
export function XPRing({ xp = 0, size = 96, strokeWidth = 8 }) {
  const { current, next, pct } = getXpProgress(xp);
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Track */}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="currentColor" strokeWidth={strokeWidth}
          className="text-muted opacity-20" />
        {/* Progress */}
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={current.color} strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          style={{ filter: `drop-shadow(0 0 6px ${current.glow})` }}
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold leading-none" style={{ color: current.color, fontFamily: 'Space Grotesk' }}>
          {current.icon}
        </span>
        <span className="text-[10px] text-muted-foreground font-medium mt-0.5">{pct}%</span>
      </div>
    </div>
  );
}

// ─── Rank Badge ───────────────────────────────────────────────────────────────
export function RankBadge({ rankName, xp, size = 'md' }) {
  const rank = ALL_RANKS.find(r => r.name === rankName) || ALL_RANKS[0];
  const cls = size === 'sm'
    ? 'text-[10px] px-2 py-0.5 gap-1'
    : 'text-xs px-2.5 py-1 gap-1.5';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-semibold ${cls}`}
      style={{
        background: `${rank.color}18`,
        borderColor: `${rank.color}40`,
        color: rank.color,
        boxShadow: `0 0 8px ${rank.glow}`,
      }}
    >
      {rank.icon} {rank.name}
    </span>
  );
}

// ─── Badge Card ───────────────────────────────────────────────────────────────
function BadgeCard({ badge, earned, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
    >
      <Card
        className={`fly-card p-3.5 relative transition-all duration-300 ${
          earned
            ? 'hover:-translate-y-0.5 hover:shadow-lg'
            : 'opacity-55'
        }`}
        style={earned ? { boxShadow: `0 0 14px ${badge.color}25, inset 0 0 0 1px ${badge.color}30` } : {}}
        data-testid={`badge-card-${badge.id.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-start gap-3">
          {/* Icon circle */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 relative"
            style={{
              background: earned ? `${badge.color}18` : 'var(--muted)',
              border: `1.5px solid ${earned ? badge.color + '40' : 'transparent'}`,
            }}
          >
            {earned ? badge.icon : '🔒'}
            {earned && (
              <div
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: badge.color }}
              >
                <Check className="w-2.5 h-2.5 text-white" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <p className="text-sm font-semibold leading-tight" style={{ fontFamily: 'Space Grotesk' }}>{badge.id}</p>
              {earned && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ background: `${badge.color}20`, color: badge.color }}>
                  Earned
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-tight">{badge.req}</p>
            <div className="flex items-center gap-1 mt-1.5" style={{ color: earned ? badge.color : '#6B7280' }}>
              <Zap className="w-2.5 h-2.5" />
              <span className="text-[10px] font-medium">+{badge.xp} XP</span>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

// ─── Badge Showcase ───────────────────────────────────────────────────────────
/** Full badge grid showing earned + locked badges */
export function BadgeShowcase({ earnedBadges = [], showLocked = true }) {
  const earned = new Set(earnedBadges);
  const toShow = showLocked ? ALL_BADGES : ALL_BADGES.filter(b => earned.has(b.id));

  const groups = [
    { key: 'flights',   label: 'Flight Milestones' },
    { key: 'community', label: 'Community' },
    { key: 'profile',   label: 'Profile' },
    { key: 'premium',   label: 'Premium' },
    { key: 'welcome',   label: 'Welcome' },
  ];

  return (
    <div className="space-y-6">
      {groups.map(grp => {
        const badges = toShow.filter(b => b.group === grp.key);
        if (!badges.length) return null;
        return (
          <div key={grp.key}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
              {grp.label}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {badges.map((badge, i) => (
                <BadgeCard
                  key={badge.id}
                  badge={badge}
                  earned={earned.has(badge.id)}
                  delay={i * 0.04}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Compact Badge Row ────────────────────────────────────────────────────────
/** Small badge pills for profile header and leaderboard */
export function BadgeRow({ badges = [], max = 4, palette }) {
  const earned = new Set(badges);
  const shown = ALL_BADGES.filter(b => earned.has(b.id)).slice(0, max);
  const extra = badges.length - shown.length;

  if (!shown.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map(b => (
        <span
          key={b.id}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-medium"
          style={{
            background: `${b.color}15`,
            borderColor: `${b.color}35`,
            color: b.color,
          }}
          title={b.req}
        >
          <span>{b.icon}</span> {b.id}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-xs px-2.5 py-1 rounded-full bg-muted border border-border text-muted-foreground font-medium">
          +{extra} more
        </span>
      )}
    </div>
  );
}

// ─── Rank Roadmap ─────────────────────────────────────────────────────────────
/** Horizontal rank tier cards showing the full progression */
export function RankRoadmap({ xp = 0, compact = false }) {
  const { current, next, pct, xpNeeded } = getXpProgress(xp);

  if (compact) {
    // Just show current rank progress + next rank
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <RankBadge rankName={current.name} xp={xp} />
          {next && (
            <span className="text-xs text-muted-foreground">
              {xpNeeded} XP to <span style={{ color: next.color }}>{next.icon} {next.name}</span>
            </span>
          )}
          {!next && <span className="text-xs font-medium text-[#FF4500]">Max Rank! 🌟</span>}
        </div>
        {/* Progress bar */}
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: current.color, boxShadow: `0 0 8px ${current.glow}` }}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
        </div>
        {/* Mini rank steps */}
        <div className="flex items-center justify-between">
          {ALL_RANKS.map((rank, i) => {
            const done = xp >= rank.xp;
            const isCur = rank.name === current.name;
            return (
              <div key={rank.name} className="flex flex-col items-center gap-0.5" title={`${rank.name} — ${rank.xp} XP`}>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all ${
                    isCur ? 'ring-2 ring-offset-1 scale-125' : ''
                  }`}
                  style={{
                    background: done ? `${rank.color}25` : 'var(--muted)',
                    borderColor: done ? rank.color : 'transparent',
                    border: done ? `1.5px solid ${rank.color}60` : '1.5px solid transparent',
                    boxShadow: isCur ? `0 0 10px ${rank.glow}` : undefined,
                    opacity: done ? 1 : 0.4,
                  }}
                >
                  {rank.icon}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Full roadmap
  return (
    <div className="space-y-1.5">
      {ALL_RANKS.map((rank, i) => {
        const done = xp >= rank.xp;
        const isCur = rank.name === current.name;
        const isNext = next && rank.name === next.name;
        const nextRank = ALL_RANKS[i + 1];
        const segPct = isCur && nextRank ? pct : done ? 100 : 0;

        return (
          <div key={rank.name}>
            <div
              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                isCur ? 'ring-1' : ''
              }`}
              style={{
                background: isCur ? `${rank.color}10` : done ? `${rank.color}06` : 'transparent',
                ringColor: isCur ? rank.color : undefined,
              }}
            >
              {/* Icon */}
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0`}
                style={{
                  background: done ? `${rank.color}18` : 'var(--muted)',
                  border: `1.5px solid ${done ? rank.color + '40' : 'transparent'}`,
                  boxShadow: isCur ? `0 0 12px ${rank.glow}` : undefined,
                  opacity: done ? 1 : 0.45,
                }}
              >
                {done ? rank.icon : <Lock className="w-4 h-4 text-muted-foreground" />}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`text-sm font-semibold ${!done ? 'text-muted-foreground' : ''}`}
                    style={{ fontFamily: 'Space Grotesk', color: done ? rank.color : undefined }}
                  >
                    {rank.name}
                  </span>
                  {isCur && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF4500]/15 text-[#FF4500] font-semibold">
                      Current
                    </span>
                  )}
                  {!isCur && done && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] font-semibold">
                      Achieved
                    </span>
                  )}
                </div>
                {isCur && (
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-1">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: rank.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${segPct}%` }}
                      transition={{ duration: 1.2, ease: 'easeOut' }}
                    />
                  </div>
                )}
              </div>

              {/* XP threshold */}
              <div className="text-right flex-shrink-0">
                <div className={`text-xs font-semibold ${done ? '' : 'text-muted-foreground'}`}
                  style={{ color: done ? rank.color : undefined }}>
                  {rank.xp} XP
                </div>
                {isCur && next && (
                  <div className="text-[10px] text-muted-foreground">{xpNeeded} to go</div>
                )}
              </div>
            </div>

            {/* Connector line */}
            {i < ALL_RANKS.length - 1 && (
              <div className="ml-8 w-0.5 h-2 bg-border" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Leaderboard Entry ────────────────────────────────────────────────────────
/** Enhanced leaderboard row for Community page */
export function LeaderboardEntry({ member, position, isCurrentUser, category }) {
  const rank = ALL_RANKS.find(r => r.name === member.rank_info?.name) || ALL_RANKS[0];
  const topBadges = (member.badges || [])
    .map(b => ALL_BADGES.find(ab => ab.id === b))
    .filter(Boolean)
    .slice(0, 2);

  const podium = [
    { bg: 'bg-amber-400', shadow: 'rgba(251,191,36,0.5)' },
    { bg: 'bg-gray-300',  shadow: 'rgba(209,213,219,0.5)' },
    { bg: 'bg-amber-700', shadow: 'rgba(180,83,9,0.5)' },
  ];
  const p = position < 3 ? podium[position] : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: position * 0.04 }}
    >
      <Card
        className={`fly-card overflow-hidden transition-all ${
          isCurrentUser
            ? 'ring-2 ring-[#FF4500] shadow-[0_0_16px_rgba(255,69,0,0.2)]'
            : position < 3 ? 'hover:-translate-y-0.5 hover:shadow-lg' : ''
        }`}
      >
        <div className="flex items-center gap-3 p-3">
          {/* Position */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
            p ? `${p.bg} text-white` : 'bg-muted text-muted-foreground'
          }`}
            style={p ? { boxShadow: `0 0 12px ${p.shadow}` } : undefined}
          >
            {position + 1}
          </div>

          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <img
              src={member.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.user_id}`}
              alt=""
              className="w-9 h-9 rounded-full object-cover"
              onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.user_id}`; }}
            />
            {/* Rank icon overlay */}
            <div
              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px]"
              style={{ background: rank.color + '20', border: `1px solid ${rank.color}60` }}
            >
              {rank.icon}
            </div>
          </div>

          {/* Name + rank */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate">{member.display_name}</span>
              {isCurrentUser && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF4500]/15 text-[#FF4500] font-bold">You</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[10px] font-medium" style={{ color: rank.color }}>
                {rank.icon} {rank.name}
              </span>
              {topBadges.length > 0 && (
                <span className="text-muted-foreground text-[10px]">·</span>
              )}
              {topBadges.map(b => (
                <span key={b.id} title={b.id} className="text-xs">{b.icon}</span>
              ))}
            </div>
          </div>

          {/* Score */}
          <div className="text-right flex-shrink-0">
            <div className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk', color: position < 3 ? rank.color : undefined }}>
              {category === 'flights' ? member.flight_count : member.xp || 0}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {category === 'flights' ? 'flights' : 'XP'}
            </div>
          </div>
        </div>

        {/* Progress strip for top 3 */}
        {position < 3 && (
          <div
            className="h-0.5 w-full opacity-40"
            style={{ background: `linear-gradient(to right, ${rank.color}, transparent)` }}
          />
        )}
      </Card>
    </motion.div>
  );
}
